/**
 * The app in English or Lithuanian. Texts are written in English in the
 * code; a catalog maps each English text to its translation, so a text
 * nobody has translated yet shows in English. "{name}" in a text is filled
 * in from values; a text that depends on a count has plural forms.
 */
export const languages = ['en', 'lt'];
/** Each language's name, in itself (for the switch). */
export const languageNames = { en: 'English', lt: 'Lietuvių' };
export const isLanguage = (value) => typeof value === 'string' && languages.includes(value);
/** The first of these language tags (e.g. navigator.languages, or Accept-Language's) that the app has, else English. */
export function pickLanguage(preferred) {
    for (const tag of preferred) {
        const code = tag.trim().toLowerCase().split(/[-_;]/)[0];
        if (isLanguage(code))
            return code;
    }
    return 'en';
}
const fill = (text, values) => values ? text.replace(/\{(\w+)\}/g, (whole, key) => (key in values ? String(values[key]) : whole)) : text;
const escape = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
export function createTranslator(language, catalog) {
    const rules = new Intl.PluralRules(language);
    let patterns = null;
    const t = (text, values) => {
        const entry = catalog[text];
        return fill(entry === undefined ? text : typeof entry === 'string' ? entry : entry.other, values);
    };
    const tp = (count, one, other, values) => {
        const all = { n: count, ...values };
        const entry = catalog[other];
        if (entry === undefined)
            return fill(count === 1 ? one : other, all);
        return fill(typeof entry === 'string' ? entry : (entry[rules.select(count)] ?? entry.other), all);
    };
    const message = (text) => {
        if (catalog[text] !== undefined)
            return t(text);
        patterns ??= Object.keys(catalog)
            .filter((key) => /\{\w+\}/.test(key))
            .map((key) => {
            const names = [];
            const source = escape(key).replace(/\\\{(\w+)\\\}/g, (_, name) => {
                names.push(name);
                return '(.+?)';
            });
            return { regex: new RegExp(`^${source}$`, 's'), names, key };
        });
        for (const { regex, names, key } of patterns) {
            const match = regex.exec(text);
            if (match)
                return t(key, Object.fromEntries(names.map((name, i) => [name, match[i + 1]])));
        }
        return text;
    };
    const tc = (context, text, values) => (catalog[`${context}|${text}`] !== undefined ? t(`${context}|${text}`, values) : t(text, values));
    return { language, t, tp, tc, message };
}
